import { Component, forwardRef, Input, OnInit, Optional, Self } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators
} from '@angular/forms';
import { DateRange } from '../../models/date-range';
import { formatDatePickerValue, parseDatePickerValue } from '../../utils/datepicker-value-parser';
import { dateRangeValidator } from '../../utils/dateRangeValidator';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbPopover
} from '@ng-bootstrap/ng-bootstrap';
import {
  GetPreviousQuarter,
  GetQuarter,
  GetQuarterEnd,
  GetQuarterStart
} from '../../utils/quarter-utils';
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears
} from 'date-fns';

@Component({
  selector: 'lib-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    }
  ],
  standalone: false
})
export class DateRangePickerComponent implements OnInit, ControlValueAccessor {
  @Input()
  public helpersPreset: 'Current' | 'current' | 'Previous' | 'previous' = 'Previous';
  @Input()
  public customHelpers: DateRangeHelper[] = [];
  @Input()
  public allowSameDate: boolean = false;

  @Input()
  skipHelpers: boolean = false;
  @Input()
  startExtraClasses: string = '';
  @Input()
  endExtraClasses: string = '';

  @Input()
  earliestDate: { year: number; month: number; day: number } | null = null;

  @Input()
  firstDayOfWeek: number = 1;

  public isDisabled = false;

  public helpers: DateRangeHelper[] = [];

  public onChange = (_: DateRange) => {};
  public onTouched = () => {};
  showHelpers = true;
  hoveredDate: NgbDate | null = null;

  fromDate: NgbDate | null;
  toDate: NgbDate | null;
  private touched = false;
  constructor(
    public formatter: NgbDateParserFormatter,
    private calendar: NgbCalendar
  ) {}

  ngOnInit(): void {
    if (this.customHelpers && this.customHelpers.length > 0) {
      this.helpers = this.customHelpers;
    }
    if (this.helpersPreset == 'Current' || this.helpersPreset == 'current') {
      this.helpers = [
        DateRangeHelperFactory.GetWeekToDate(),
        DateRangeHelperFactory.GetMonthToDate(),
        DateRangeHelperFactory.GetQuarterToDate(),
        DateRangeHelperFactory.GetYearToDate()
      ];
    } else {
      this.helpers = [
        DateRangeHelperFactory.GetLastWeek(),
        DateRangeHelperFactory.GetLastMonth(),
        DateRangeHelperFactory.GetLastQuarter(),
        DateRangeHelperFactory.GetLastYear()
      ];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(obj: DateRange): void {
    if (obj != null) {
      if (obj.start != null) {
        const parsedStartDate = this._convertToNgbDate(obj.start);
        if (this.fromDate == null || !this.fromDate.equals(parsedStartDate)) {
          this.fromDate = parsedStartDate;
        }
      } else {
        this.fromDate = null;
      }
      if (obj.end != null) {
        const parsedEndDate = this._convertToNgbDate(obj.end);
        if (this.toDate == null || !this.toDate.equals(parsedEndDate)) {
          this.toDate = parsedEndDate;
        }
      } else {
        this.toDate = null;
      }
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
    this._setShowHelpers();
  }

  onDateSelection(date: NgbDate, p: NgbPopover) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (
      this.fromDate &&
      !this.toDate &&
      date &&
      (date.after(this.fromDate) || this.allowSameDate)
    ) {
      this.toDate = date;
      p.close();
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    this._valueChanged();
    this._markTouched();
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }
  isStart(date: NgbDate) {
    return date.equals(this.fromDate);
  }
  isEnd(date: NgbDate) {
    return this.toDate && date.equals(this.toDate);
  }
  setStartInput(val) {
    this.fromDate = this.validateInput(this.fromDate, val);
    this._valueChanged();
    this._markTouched();
  }
  setEndInput(val) {
    this.toDate = this.validateInput(this.toDate, val);
    this._valueChanged();
    this._markTouched();
  }

  validateInput(currentValue: NgbDate | null, input: string): NgbDate | null {
    if (input == null || input == '') {
      return null;
    }
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed))
      ? NgbDate.from(parsed)
      : currentValue;
  }
  showHelperScreen() {
    return this.showHelpers;
  }
  helperClicked(helper: DateRangeHelper, p: NgbPopover) {
    p.close();
    const startParsed = formatDatePickerValue(helper.start);
    const startStruct = new NgbDate(startParsed.year, startParsed.month, startParsed.day);
    const endParsed = formatDatePickerValue(helper.end);
    const endStruct = new NgbDate(endParsed.year, endParsed.month, endParsed.day);
    this.onDateSelection(startStruct, p);
    this.onDateSelection(endStruct, p);
    this.showHelpers = false;
  }
  customDateClicked() {
    this.showHelpers = false;
  }
  openPopover(p: NgbPopover) {
    this._setShowHelpers();
    p.toggle();
  }
  clearDates() {
    this.fromDate = null;
    this.toDate = null;
    this._valueChanged();
    this._markTouched();
  }

  private _convertToNgbDate(date: Date) {
    const parsed = formatDatePickerValue(date);
    return new NgbDate(parsed.year, parsed.month, parsed.day);
  }
  private _valueChanged() {
    const value = {
      start: this.fromDate != null ? parseDatePickerValue(this.fromDate) : null,
      end: this.toDate != null ? parseDatePickerValue(this.toDate) : null
    };
    if (value.start == null || value.end == null) {
      this.onChange(null);
    } else {
      this.onChange(value);
    }
  }
  private _setShowHelpers() {
    this.showHelpers = !this.skipHelpers && this.fromDate == null && this.toDate == null;
  }
  private _markTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }
}

export interface DateRangeHelper {
  name: string;
  start: Date;
  end: Date;
}

export class DateRangeHelperFactory {
  public static GetWeekToDate(): DateRangeHelper {
    const now = new Date();
    return {
      name: 'This Week',
      start: DateRangeHelperFactory._startOfWeek(now),
      end: now
    };
  }
  public static GetMonthToDate(): DateRangeHelper {
    const now = new Date();
    return {
      name: 'Month to Date',
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: now
    };
  }
  public static GetQuarterToDate(): DateRangeHelper {
    const now = new Date();
    const currentQuarter = GetQuarter(now);
    return {
      name: 'Quarter to Date',
      start: GetQuarterStart(currentQuarter, now.getFullYear()),
      end: now
    };
  }
  public static GetYearToDate(): DateRangeHelper {
    const now = new Date();
    return {
      name: 'Year To Date',
      start: new Date(now.getFullYear(), 0, 1),
      end: now
    };
  }
  public static GetLastWeek() {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    return {
      name: 'Last Week',
      start: startOfWeek(lastWeek),
      end: endOfWeek(lastWeek)
    };
  }
  public static GetLastMonth() {
    const now = new Date();
    const oneMonthAgo = subMonths(now, 1);
    return {
      name: 'Last Month',
      start: startOfMonth(oneMonthAgo),
      end: endOfMonth(oneMonthAgo)
    };
  }
  public static GetLastQuarter() {
    const now = new Date();
    const lastQuarter = subQuarters(now, 1);
    return {
      name: 'Last Quarter',
      start: startOfQuarter(lastQuarter),
      end: endOfQuarter(lastQuarter)
    };
  }
  public static GetLastYear() {
    const now = new Date();
    const lastYear = subYears(now, 1);
    return {
      name: 'Last Year',
      start: startOfYear(lastYear),
      end: endOfYear(lastYear)
    };
  }

  private static _startOfWeek(date) {
    const lastWeek = subWeeks(date, 1);
    return startOfWeek(lastWeek);
  }
  private static _endOfWeek(date) {
    const lastWeek = subWeeks(date, 1);
    return endOfWeek(lastWeek);
  }
}
